// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { DataTable } from '@/components/organism/Table';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import Autocomplete from '@/components/elements/AutoComplete';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import { useDebounce } from '@/hooks/useDebounce';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  STRINGS,
  ALL_PROVIDER,
  ALL_OPERATOR,
  DROPDOWN_OPTIONS,
  SortEnum,
} from '@/constant/global/global.constant';
import {
  getCoolerTransactionsFailure,
  getCoolerTransactionFailure,
  getCoolerTransactions,
  getCoolerTransaction,
  resetCoolerTransactions,
} from '@/store/slices/coolerTransaction.slice';
import {
  getCoolerSingleTransaction,
  getCoolerTransactionList,
} from '@/store/selectors/coolerTransaction.selector';
import { CoolerTransactionExpandedPanel } from '@/components/organism/TableExpand/CoolerTransactionExpanded/CoolerTransactionExpandedPanel';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { COOLER_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/coolerTransaction.testIds';
import { tid } from '@/utils/testId';
import { ICoolerTransaction } from './coolerTransaction.interface';
import {
  COOLER_TRANSACTION_PROPS,
  COOLER_TRANSACTION_TYPES,
  COOLER_TRANSACTION_STATUS,
  COOLER_TRANSACTION_STATUS_CODES,
  getStatusCodeColor,
  getTransactionTypeColor,
} from './constant';
import { buildColumns } from './coolerTransaction.columns';

/* ── page ─────────────────────────────────────────────────────────────── */

const CoolerTransaction = () => {
  const dispatch = useDispatch();
  const providerState = useSelector((state: any) => state.provider);
  const providerList = providerState?.providers?.data ?? [];

  const [isCopy, setIsCopy] = useState('');
  const [searchByTransactionId, onSearchByTransactionId] = useState('');
  const [selectedType, setSelectedType] = useState(COOLER_TRANSACTION_TYPES[0]);
  const [selectedStatus, setSelectedStatus] = useState(
    COOLER_TRANSACTION_STATUS[0],
  );
  const [selectedStatusCode, setSelectedStatusCode] = useState(
    COOLER_TRANSACTION_STATUS_CODES[0],
  );
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: SortEnum.desc,
  });
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const hasLoaded = useRef(false);

  const [selection, setSelection] = useState<{
    provider: any;
    operator: any;
    market: any;
    device: any;
  }>({ provider: null, operator: null, market: null, device: null });

  const transactionIdQuery = useDebounce(
    searchByTransactionId.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  // Clear stale data on unmount so next visit starts with empty state
  useEffect(() => {
    return () => {
      dispatch(resetCoolerTransactions());
    };
  }, []);

  const { coolerTransactions: listData, loading: listLoading } = useSelector(
    getCoolerTransactionList,
  );
  const { coolerTransaction: singleData, loading: singleLoading } = useSelector(
    getCoolerSingleTransaction,
  );

  const data: ICoolerTransaction[] = listData?.data ?? [];
  const paginationResponse = listData?.pagination ?? {
    totalRecords: 0,
    totalPages: 0,
  };

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && data.length > 0) hasLoaded.current = true;
  }, [data]);

  const totalPages = useMemo(
    () => Math.ceil(paginationResponse.totalRecords / pagination.limit),
    [paginationResponse.totalRecords, pagination.limit],
  );

  const isSearching = Boolean(transactionIdQuery);
  const displayData: ICoolerTransaction[] = isSearching
    ? singleData?.success
      ? [singleData.data]
      : []
    : data;

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 2000);
  }, []);

  const handlePageSizeChange = useCallback((value: number) => {
    setPagination((p) => ({ ...p, limit: value }));
  }, []);
  const handlePreviousClick = useCallback((page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  }, []);
  const handleNextClick = useCallback(
    (page: number) => {
      if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
    },
    [totalPages],
  );

  const handleSelectionChange = useCallback(
    ({ provider, operator, market, device }: any) => {
      setSelection({ provider, operator, market, device });
      setPagination((p) => ({ ...p, page: 1 }));
    },
    [],
  );

  const buildParams = useCallback(
    () => ({
      ...sorting,
      ...pagination,
      ...(selection?.operator?.id !== ALL_OPERATOR.id
        ? { operatorId: selection?.operator?.sourceId }
        : {}),
      ...(selectedType?.id !== COOLER_TRANSACTION_TYPES[0].id
        ? { type: selectedType?.id }
        : {}),
      ...(selectedStatus?.id !== COOLER_TRANSACTION_STATUS[0].id
        ? { status: selectedStatus?.id }
        : {}),
      ...(selectedStatusCode?.id !== COOLER_TRANSACTION_STATUS_CODES[0].id
        ? { statusCode: selectedStatusCode?.id }
        : {}),
      ...(selection?.device?.id !==
        DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION?.id
        ? { deviceId: selection?.device?.sourceId }
        : {}),
      ...(selection?.market?.sourceId !==
        DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION?.sourceId
        ? { marketId: selection?.market?.sourceId }
        : {}),
      ...(selection?.provider?.id !== ALL_PROVIDER.id
        ? { partnerUid: selection?.provider?.partnerUid }
        : {}),
    }),
    [
      sorting,
      pagination,
      selection,
      selectedType,
      selectedStatus,
      selectedStatusCode,
    ],
  );

  useEffect(() => {
    if (selection?.provider) {
      try {
        dispatch(getCoolerTransactions(buildParams()));
      } catch (err: any) {
        dispatch(getCoolerTransactionsFailure(err?.response?.data));
      }
    }
  }, [
    selectedType?.id,
    selectedStatus?.id,
    selectedStatusCode?.id,
    selection,
    sorting,
    pagination.page,
    pagination.limit,
  ]);

  useEffect(() => {
    if (transactionIdQuery && transactionIdQuery.length >= 3) {
      try {
        dispatch(
          getCoolerTransaction({
            ...buildParams(),
            transactionId: transactionIdQuery,
          }),
        );
      } catch (err: any) {
        dispatch(getCoolerTransactionFailure(err?.response?.data));
      }
    }
  }, [transactionIdQuery]);

  const columns = useMemo(
    () => buildColumns(isCopy, onCopyClick, providerList),
    [isCopy, onCopyClick, providerList],
  );

  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.COOLER_TRANSACTIONS}
          subtitle={STRINGS.COOLER_TRANSACTIONS_SUB}
          icon={getNavItemIcon(NAV_ITEMS.COOLER_TRANSACTIONS)}
          actionsSlot={
            <GlobalSearch
              className="w-full md:w-[220px]"
              placeholder={COOLER_TRANSACTION_PROPS.LABELS.TRANSACTION_ID}
              dataTestId={tid(TID.SEARCH_INPUT)}
              globalSearch={searchByTransactionId}
              setGlobalSearch={onSearchByTransactionId}
            />
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleSelectionChange}
              showProvider
              showLocation={false}
              showOperator
              showDevice
              showMarket
              dataTestId={tid(TID.FILTER_DROPDOWN)}
            />
          }
          filtersRight={
            <div className="flex items-center gap-2">
              <div className="w-[140px]">
                <Autocomplete
                  label={COOLER_TRANSACTION_PROPS.LABELS.TYPE}
                  placeholder={COOLER_TRANSACTION_PROPS.FILTERS.TYPE}
                  dataTestId={tid(TID.TYPE_FILTER)}
                  selectedValue={selectedType}
                  handleSelectedValue={(v: any) => {
                    setSelectedType(v);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  lists={COOLER_TRANSACTION_TYPES}
                />
              </div>
              <div className="w-[140px]">
                <Autocomplete
                  label={COOLER_TRANSACTION_PROPS.LABELS.STATUS}
                  placeholder={COOLER_TRANSACTION_PROPS.FILTERS.STATUS}
                  dataTestId={tid(TID.STATUS_FILTER)}
                  selectedValue={selectedStatus}
                  handleSelectedValue={(v: any) => {
                    setSelectedStatus(v);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  lists={COOLER_TRANSACTION_STATUS}
                />
              </div>
              <div className="w-[160px]">
                <Autocomplete
                  label={COOLER_TRANSACTION_PROPS.LABELS.STATUS_CODE}
                  placeholder={COOLER_TRANSACTION_PROPS.FILTERS.STATUS_CODE}
                  dataTestId={tid(TID.STATUS_CODE_FILTER)}
                  selectedValue={selectedStatusCode}
                  handleSelectedValue={(v: any) => {
                    setSelectedStatusCode(v);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  lists={COOLER_TRANSACTION_STATUS_CODES}
                />
              </div>
            </div>
          }
        />
      }
    >
      <DataTable
        data-testid={tid(TID.TABLE)}
        data={displayData}
        columns={columns}
        loading={listLoading}
        skeletonRows={15}
        onSort={(orderBy, order) =>
          setSorting({ orderBy, order: order as SortEnum })
        }
        sortState={sorting}
        rowKey={(item: any) => item?.transactionId ?? ''}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
        pagination={
          !isEmpty(displayData)
            ? {
              state: pagination,
              totalItem: paginationResponse.totalRecords,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
        expandedRowIndex={expandedRowIndex}
        onExpandChange={(idx, item) => {
          if (item) {
            dispatch(
              getCoolerTransaction({
                transactionId: (item as ICoolerTransaction).transactionId,
              }),
            );
          }
          setExpandedRowIndex(idx);
        }}
        renderExpandedRow={(item: ICoolerTransaction, rowIndex: number) => (
          <CoolerTransactionExpandedPanel
            row={item}
            rowIndex={rowIndex}
            data={singleData?.data}
            loading={singleLoading}
          />
        )}
      />
    </FullPageCard>
  );
};

export default CoolerTransaction;
